/**
 * JourneySelector
 *
 * SHS Visual Experience System — Sprint 29.2C.
 *
 * The first user-orientation layer of the SHS platform. Presents three
 * primary consumer entry paths in a single scannable section:
 *
 *   1. Understand My Sleep    → /sleep-assessment       (Sleep Assessment)
 *   2. Improve My Treatment   → /cpap-adherence         (CPAP Journey — canonical PAP support)
 *   3. Learn About Sleep      → /academy                (Academy Landing → Sleep Reset)
 *
 * Governance:
 *   - Additive to the existing homepage. Does NOT replace Start Here,
 *     Features, SRA Featured CTA, or any other section (per Sprint 29.2C
 *     directive).
 *   - Uses the existing Visual Experience System (RiseIn) — no new
 *     animation dependency.
 *   - Reduced-motion honored automatically inside RiseIn.
 *   - Full semantic keyboard/focus support via <Link>-wrapped cards
 *     (each card = one interactive element, no nested links).
 *   - Bilingual content follows the existing HomePage pattern
 *     (per-page bilingual object driven by useLanguage).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Moon, HeartPulse, GraduationCap } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { RiseIn } from '../experience';

const getJourneyContent = (language) => {
    const isEs = language === 'es';
    return {
        eyebrow: isEs ? 'Elige tu camino' : 'Choose your path',
        heading: isEs ? '¿Qué te trae por aquí hoy?' : 'What brings you here today?',
        subheading: isEs
            ? 'Tres caminos claros para comenzar con Sleep Health Services.'
            : 'Three clear starting points across Sleep Health Services.',
        journeys: [
            {
                id: 'understand',
                icon: Moon,
                title: isEs ? 'Entender mi sueño' : 'Understand my sleep',
                description: isEs
                    ? 'Explora tus patrones de sueño y descubre qué áreas podrían merecer atención.'
                    : 'Explore your sleep patterns and discover what may deserve attention.',
                cta: isEs ? 'Comenzar evaluación' : 'Start Sleep Assessment',
                to: '/sleep-assessment',
                accent: 'sky',
            },
            {
                id: 'treatment',
                icon: HeartPulse,
                title: isEs ? 'Mejorar mi tratamiento' : 'Improve my treatment',
                description: isEs
                    ? 'Obtén educación y apoyo para tu terapia PAP y tu proceso de tratamiento del sueño.'
                    : 'Get education and support for PAP therapy and your sleep-treatment journey.',
                cta: isEs ? 'Explorar apoyo de tratamiento' : 'Explore Treatment Support',
                to: '/cpap-adherence',
                accent: 'emerald',
            },
            {
                id: 'learn',
                icon: GraduationCap,
                title: isEs ? 'Aprender sobre el sueño' : 'Learn about sleep',
                description: isEs
                    ? 'Desarrolla conocimientos prácticos sobre el sueño mediante educación guiada y Sleep Reset Academy.'
                    : 'Build practical sleep knowledge through guided education and the Sleep Reset Academy.',
                cta: isEs ? 'Explorar Sleep Reset Academy' : 'Explore Sleep Reset Academy',
                to: '/academy/sleep-reset',
                accent: 'indigo',
            },
        ],
    };
};

// Per-accent Tailwind utility maps. Kept static so Tailwind's JIT can see
// every literal at build time and never purge them.
const ACCENT = {
    sky: {
        iconBg: 'bg-sky-50 text-sky-600',
        iconRing: 'ring-sky-200',
        hoverBorder: 'hover:border-sky-300',
        focusRing: 'focus-visible:ring-sky-400',
        ctaText: 'text-sky-700 group-hover:text-sky-900',
    },
    emerald: {
        iconBg: 'bg-emerald-50 text-emerald-600',
        iconRing: 'ring-emerald-200',
        hoverBorder: 'hover:border-emerald-300',
        focusRing: 'focus-visible:ring-emerald-400',
        ctaText: 'text-emerald-700 group-hover:text-emerald-900',
    },
    indigo: {
        iconBg: 'bg-indigo-50 text-indigo-600',
        iconRing: 'ring-indigo-200',
        hoverBorder: 'hover:border-indigo-300',
        focusRing: 'focus-visible:ring-indigo-400',
        ctaText: 'text-indigo-700 group-hover:text-indigo-900',
    },
};

const JourneySelector = () => {
    const { language } = useLanguage();
    const content = getJourneyContent(language);

    return (
        <section
            data-testid="journey-selector-section"
            aria-labelledby="journey-selector-heading"
            className="bg-white py-14 md:py-20"
        >
            <div className="container mx-auto px-4">
                <RiseIn className="max-w-6xl mx-auto">
                    <div className="text-center mb-10 md:mb-12">
                        <p
                            className="text-xs md:text-sm font-semibold tracking-wider uppercase text-slate-500 mb-3"
                            data-testid="journey-selector-eyebrow"
                        >
                            {content.eyebrow}
                        </p>
                        <h2
                            id="journey-selector-heading"
                            className="text-3xl md:text-4xl font-bold text-slate-900"
                            data-testid="journey-selector-heading"
                        >
                            {content.heading}
                        </h2>
                        <p
                            className="mt-3 text-base md:text-lg text-slate-600 max-w-2xl mx-auto"
                            data-testid="journey-selector-subheading"
                        >
                            {content.subheading}
                        </p>
                    </div>

                    <ul
                        className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6"
                        data-testid="journey-selector-grid"
                    >
                        {content.journeys.map((j) => {
                            const Icon = j.icon;
                            const accent = ACCENT[j.accent];
                            return (
                                <li key={j.id}>
                                    <Link
                                        to={j.to}
                                        data-testid={`journey-card-${j.id}`}
                                        className={[
                                            'group block h-full rounded-2xl border border-slate-200 bg-white p-6 md:p-7',
                                            'transition-[transform,box-shadow,border-color] duration-200',
                                            'hover:-translate-y-0.5 hover:shadow-md',
                                            accent.hoverBorder,
                                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                                            accent.focusRing,
                                            'motion-reduce:hover:transform-none motion-reduce:transition-none',
                                        ].join(' ')}
                                    >
                                        <div
                                            className={[
                                                'inline-flex items-center justify-center w-11 h-11 rounded-xl ring-1 mb-4',
                                                accent.iconBg,
                                                accent.iconRing,
                                            ].join(' ')}
                                            aria-hidden="true"
                                        >
                                            <Icon className="w-5 h-5" strokeWidth={1.75} />
                                        </div>
                                        <h3 className="text-lg md:text-xl font-semibold text-slate-900 mb-2">
                                            {j.title}
                                        </h3>
                                        <p className="text-sm md:text-[15px] text-slate-600 leading-relaxed">
                                            {j.description}
                                        </p>
                                        <span
                                            className={[
                                                'mt-5 inline-flex items-center gap-1 text-sm font-semibold',
                                                accent.ctaText,
                                                'transition-colors',
                                            ].join(' ')}
                                            data-testid={`journey-cta-${j.id}`}
                                        >
                                            {j.cta}
                                            <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5 motion-reduce:group-hover:transform-none">→</span>
                                        </span>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </RiseIn>
            </div>
        </section>
    );
};

export default JourneySelector;
